import React, { useState, useEffect } from 'react';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getApplications, getJobs } from '../services/api';

const HrAnalyticsPage = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  // Get HR company name from session
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const companyName = getHrCompanyName();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appsData, jobsData] = await Promise.all([
        getApplications({ company: companyName }),
        getJobs({ company: companyName })
      ]);
      
      setApplications(appsData);
      setJobs(jobsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const totalApplications = applications.length;
    const interviewedCount = applications.filter(app => 
      app.status === 'interview' || app.status === 'offer' || app.status === 'rejected'
    ).length;
    const offeredCount = applications.filter(app => app.status === 'offer').length;
    const rejectedCount = applications.filter(app => app.status === 'rejected').length;
    
    const conversionRate = totalApplications > 0 
      ? ((offeredCount / totalApplications) * 100).toFixed(1)
      : 0;
    
    const interviewRate = totalApplications > 0
      ? ((interviewedCount / totalApplications) * 100).toFixed(1)
      : 0;

    const avgTimeToHire = applications
      .filter(app => app.status === 'offer' && app.applied_at)
      .reduce((acc, app) => {
        const days = Math.floor((new Date() - new Date(app.applied_at)) / (1000 * 60 * 60 * 24));
        return acc + days;
      }, 0) / (offeredCount || 1);

    return {
      totalApplications,
      interviewedCount,
      offeredCount,
      rejectedCount,
      conversionRate,
      interviewRate,
      avgTimeToHire: Math.round(avgTimeToHire)
    };
  };

  const metrics = calculateMetrics();

  const statusDistribution = [
    { status: 'Applied', count: applications.filter(a => a.status === 'applied').length, color: 'bg-gray-500' },
    { status: 'Review', count: applications.filter(a => a.status === 'review').length, color: 'bg-gray-600' },
    { status: 'Interview', count: applications.filter(a => a.status === 'interview').length, color: 'bg-gray-700' },
    { status: 'Offer', count: applications.filter(a => a.status === 'offer').length, color: 'bg-green-500' },
    { status: 'Rejected', count: applications.filter(a => a.status === 'rejected').length, color: 'bg-red-500' },
  ];

  const topJobs = jobs
    .map(job => ({
      ...job,
      applicationCount: applications.filter(app => app.job_id === job.id).length
    }))
    .sort((a, b) => b.applicationCount - a.applicationCount)
    .slice(0, 5);

  if (loading) {
    return (
      <HrDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </HrDashboardLayout>
    );
  }

  return (
    <HrDashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">Hiring metrics and performance insights</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="description" size={24} className="text-gray-700" />
              </div>
            </div>
            <p className="text-sm text-gray-600">Total Applications</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{metrics.totalApplications}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="event" size={24} className="text-gray-700" />
              </div>
            </div>
            <p className="text-sm text-gray-600">Interview Rate</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{metrics.interviewRate}%</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="check_circle" size={24} className="text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600">Conversion Rate</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{metrics.conversionRate}%</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="schedule" size={24} className="text-gray-700" />
              </div>
            </div>
            <p className="text-sm text-gray-600">Avg. Time to Hire</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{metrics.avgTimeToHire} days</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Status Distribution</h3>
            <div className="space-y-4">
              {statusDistribution.map((item) => {
                const percentage = metrics.totalApplications > 0 
                  ? ((item.count / metrics.totalApplications) * 100).toFixed(1)
                  : 0;
                return (
                  <div key={item.status}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{item.status}</span>
                      <span className="text-sm text-gray-600">{item.count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`${item.color} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Performing Jobs */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Jobs</h3>
            <div className="space-y-4">
              {topJobs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No jobs data available</p>
              ) : (
                topJobs.map((job, index) => (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{job.title}</p>
                        <p className="text-xs text-gray-500">{job.location}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{job.applicationCount}</p>
                      <p className="text-xs text-gray-500">applications</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Hiring Funnel */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Hiring Funnel</h3>
          <div className="flex items-center justify-between">
            {[
              { label: 'Applied', count: metrics.totalApplications, icon: 'description' },
              { label: 'Reviewed', count: applications.filter(a => a.status !== 'applied').length, icon: 'rate_review' },
              { label: 'Interviewed', count: metrics.interviewedCount, icon: 'event' },
              { label: 'Offered', count: metrics.offeredCount, icon: 'check_circle' },
            ].map((stage, index) => (
              <React.Fragment key={stage.label}>
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name={stage.icon} size={28} className="text-gray-700" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stage.count}</p>
                  <p className="text-sm text-gray-600 mt-1">{stage.label}</p>
                </div>
                {index < 3 && (
                  <div className="flex-shrink-0 px-4">
                    <Icon name="arrow_forward" size={24} className="text-gray-400" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </HrDashboardLayout>
  );
};

export default HrAnalyticsPage;
